import {Radio} from 'antd'
import {exam_getExaminations_Api} from 'apis'
import classNames from 'classnames'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useState} from 'react'
import {branch, compose, renderNothing} from 'recompose'
import AntdConfigWrapper, {RenderEmptyMeta,} from 'views/AppLocals/custom/AntdConfigWrapper'
import {reactSelectComponents, SelectWidget,} from 'views/AppLocals/custom/ReactSelectComponents'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {getResponseItems, NullAsDefault} from 'views/Shared'

const FilterSelectField = ({
  name,
  value,
  onChange,
  options,
  isLoading,
  className,
  ...props
}) => (
  <AntdConfigWrapper renderEmpty={RenderEmptyMeta}>
    <SelectWidget
      value={_.find(options, { value }) ? value : undefined}
      showSearch={true}
      allowClear={true}
      onChange={(value) => {
        onChange(name, value)
      }}
      loading={!!isLoading}
      optionFilterProp="label"
      className={classNames('CenterSelector w-full', className)}
      options={!!isLoading ? [] : options}
      suffixIcon={
        !!isLoading ? undefined : <reactSelectComponents.DropdownIndicator />
      }
      {...props}
    />
  </AntdConfigWrapper>
)

const ExaminationsAsyncWrapper = compose(
  branch(
    ({ owner_id, owner_type }) => _.some([owner_id, owner_type], _.isEmpty),
    renderNothing
  )
)(({ owner_id, owner_type, children }) => {
  const [values, setValues] = useState()

  const apiInfo = exam_getExaminations_Api

  const { response, isLoading } = useAsyncWithCache({
    cacheId: [
      apiInfo.path.replace(':id', owner_id).replace(':type', owner_type),
      JSON.stringify(values),
    ].join('/'),
    apiInfo,
    values,
    deps: [values],
    query: bindQueryParams([{ id: owner_id }, { type: owner_type }]),
  })

  return _.isFunction(children)
    ? children({
        isLoading,
        items: getResponseItems(response),
        onSearch: (keyword) => {
          setValues({
            keyword,
          })
        },
      })
    : children
})

export const ExaminationsFilter = ({
  owner_id,
  owner_type,
  defaultValue,
  value: filter = {
    examination_id: undefined,
  },
  onChange: setValues = NullAsDefault,
}) => {
  const t = useTranslate()

  const handleChange = useCallback(
    (name, value) => {
      setValues({
        ...filter,
        [name]: value,
      })
    },
    [filter]
  )

  return (
    <div className="flex flex-col items-center gap-2 mt-2">
      <ExaminationsAsyncWrapper
        {...{
          owner_id,
          owner_type,
        }}>
        {({ items, isLoading }) => {
          const options = Array.from(items).map(({ id, ...item }) => ({
            value: id,
            label: t(getTitle(item)),
          }))
          return (
            <div className="flex items-center gap-2 w-full">
              <span className="text-sm text-color-400 italic whitespace-no-wrap">
                  {`${t('choose examination')} : `}
              </span>
              <FilterSelectField
                name="examination_id"
                loading={isLoading}
                value={filter.examination_id}
                placeholder={t(isLoading ? 'loading...' : 'examination')}
                options={options}
                onChange={(name, value) => {
                  handleChange(name, value)
                }}
              />
            </div>
          )
        }}
      </ExaminationsAsyncWrapper>
    </div>
  )
}

export const ExaminationPaperFilter = ({
  owner_id,
  owner_type,
  defaultValue,
  value: filter = {
    result_status: undefined,
    examination_id: undefined,
  },
  onChange: setValues = NullAsDefault,
}) => {
  const t = useTranslate()

  const handleChange = useCallback(
    (name, value) => {
      setValues({
        ...filter,
        [name]: value,
      })
    },
    [filter]
  )

  return (
    <div className="flex flex-col md:flex-row gap-2 mt-2">
      <ExaminationsAsyncWrapper
        {...{
          owner_id,
          owner_type,
        }}>
        {({ items, isLoading }) => {
          const options = Array.from(items).map(({ id, ...item }) => ({
            value: id,
            label: t(getTitle(item)),
          }))
          return (
            <div className="flex items-center gap-2 w-full">
              <span className="text-sm text-color-400 italic whitespace-no-wrap">
                  {`${t('choose examination')} : `}
              </span>
              <FilterSelectField
                name="examination_id"
                loading={isLoading}
                value={filter.examination_id}
                placeholder={t(isLoading ? 'loading...' : 'examination')}
                options={options}
                onChange={(name, value) => {
                  handleChange(name, value)
                }}
              />
            </div>
          )
        }}
      </ExaminationsAsyncWrapper>
      <Radio.Group
        name="result_status"
        optionType="button"
        buttonStyle="solid"
        className="flex items-center custom-radio-button justify-end w-full lg:w-auto"
        onChange={(event) => {
          const { name, value } = event.target
          handleChange(name, value)
        }}
        defaultValue={filter.result_status || undefined}>
        <Radio value={undefined}>{t('all')}</Radio>
        <Radio value={'passed'}>{t('passed')}</Radio>
        <Radio value={'failed'}>{t('failed')}</Radio>
      </Radio.Group>
    </div>
  )
}

export default ExaminationsFilter
